import React, { useEffect, useState, useMemo } from 'react';
import { fetchHiringRequests, GetDemandPlanning,  fetchHiringManagers, fetchActiveDepartments } from '../services/apiService';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import HiringRequestCards from '../components/HiringRequestCards';
import { getDepartmentIdFromToken, getToken, getUserIDFromToken } from '../services/authService';

 const HiringRequest = () => {
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const cardFilter = navState?.filter || "all";
  const [hiringRequests, setHiringRequests] = useState([]);
  const [demandPlans, setDemandPlans] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [hiringManagers, setHiringManagers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [planDateFrom, setPlanDateFrom] = useState('');
  const [planDateTo, setPlanDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(8000);
  const [filtersExpanded, setFiltersExpanded] = useState(false); // ✅ collapsible filters

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: '#fff',
      borderRadius: '0.75rem',
      borderColor: state.isFocused ? 'rgb(18,76,114)' : 'rgba(18,76,114,0.25)',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(18,76,114,0.12)' : 'none',
      padding: '1px 6px',
      cursor: 'pointer',
      minHeight: '38px',
      '&:hover': { borderColor: 'rgb(18,76,114)' }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.75rem',
      boxShadow: '0 10px 25px rgba(18,76,114,0.15)',
      zIndex: 1000,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'rgba(18,76,114,0.1)' : state.isFocused ? 'rgba(18,76,114,0.05)' : 'white',
      color: 'rgb(18,76,114)',
      cursor: 'pointer',
      padding: '8px 12px',
    }),
    singleValue: (provided) => ({ ...provided, color: 'rgb(18,76,114)', fontWeight: '600' }),
    dropdownIndicator: (provided) => ({ ...provided, color: 'rgba(18,76,114,0.5)', ':hover': { color: 'rgb(18,76,114)' } }),
    indicatorSeparator: () => ({ display: 'none' }),
    placeholder: (provided) => ({ ...provided, color: 'rgba(18,76,114,0.4)', fontSize: '14px' }),
  };

  useEffect(() => {
    const getData = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const role = getToken();
        const departmentId = getDepartmentIdFromToken();
        const [requests, plans, deptData, managers] = await Promise.all([
          fetchHiringRequests(), GetDemandPlanning(currentYear),
          fetchActiveDepartments(), fetchHiringManagers()
        ]);
        setHiringManagers(managers);
        const userId = getUserIDFromToken();
        let filteredRequests = requests;

        // Role-specific filtering (same as HomePage)
        if (role.role === "Finance Manager") {
          filteredRequests = requests.filter(req => {
            const needsApproval = req.isFinanceManagerApproveRequired === true && req.isFinanceManagerApproved === null && (req.status === "Submitted" || req.status === "Approved");
            const sameDepartment = String(req.department_id) === String(departmentId);
            return needsApproval || sameDepartment;
          });
        } else if (role.role === "HR Director") {
          filteredRequests = requests.filter(req => {
            const needsChainApproval = !!req.isFinanceManagerApproveRequired && !!req.isFinanceManagerApproved && !req.isHRDirectorApproved && req.status === "Approved";
            const fullyApproved = !!req.isFinanceManagerApproveRequired && !!req.isCEOApproved && !req.isConevrtToSource;
            const needsForecastApproval = req.status === "Submitted" && req.request_type === "Forecast";
            const forecastApproved = !req.isFinanceManagerApproveRequired && req.status === "Approved";
            const converted = req.isConevrtToSource === true;
            return needsChainApproval || fullyApproved || needsForecastApproval || forecastApproved || converted;
          });
        } else if (role.role === "CEO") {
          filteredRequests = requests.filter(req => {
            const needsCEOApproval = !!req.isFinanceManagerApproveRequired && !!req.isHRDirectorApproved && !req.isCEOApproved && req.status === "Approved";
            const isOwnRequest = Number(req.hiring_manager) === Number(userId);
            return needsCEOApproval || isOwnRequest;
          });
        } else if (role.role === "HR Specialist") {
          const uid = Number(userId);
          filteredRequests = requests.filter(req => req.recruiter_id === uid);
        } else if (role.IsDepartmentRestricted === "True") {
          filteredRequests = requests.filter(req => String(req.department_id) === String(departmentId));
        } else {
          filteredRequests = requests.filter(req => req.isDepartmentRestricted ? req.status !== "Draft" : true);
        }

        // Apply card filter from HomePage FlipCard click
        if (cardFilter === "hr-pending") {
          filteredRequests = filteredRequests.filter(req => {
            const forecastPending = req.status === "Submitted" && req.request_type === "Forecast";
            const excPendingHRDir = (req.request_type === "Exceptional" || req.request_type === "Outsourced") &&
              !!req.isFinanceManagerApproved && !req.isHRDirectorApproved;
            return forecastPending || excPendingHRDir;
          });
        } else if (cardFilter === "sourcing") {
          filteredRequests = filteredRequests.filter(req => req.isConevrtToSource === true);
        } else if (cardFilter === "waiting-hiring") {
          filteredRequests = filteredRequests.filter(req =>
            ["Under Hiring Process", "Send For Approval"].includes(req.status)
          );
        }

        setHiringRequests(filteredRequests);
        setDemandPlans(role.IsDepartmentRestricted === "True"
          ? plans.filter(plan => String(plan.department_id) === String(departmentId)) : plans);
        setDepartments(role.IsDepartmentRestricted === "True"
          ? deptData.filter(d => String(d.department_id) === String(departmentId)) : deptData);
      } catch (error) {
        console.error(error);
        toast.error("Error loading data");
      } finally {
        setIsLoading(false);
      }
    };
    getData();
  }, []);

  const filteredHiringRequests = useMemo(() => {
    return hiringRequests.filter(req => {
      const deptMatch = selectedDepartment === 'all' ? true : Number(req.department_id) === Number(selectedDepartment);
      const statusMatch = selectedStatus === 'all' ? true : req.status?.trim() === selectedStatus;
      const planDate = req.plan_date ? new Date(req.plan_date) : null;
      const fromMatch = planDateFrom ? planDate >= new Date(planDateFrom) : true;
      const toMatch = planDateTo ? planDate <= new Date(planDateTo) : true;
      const searchMatch = searchTerm
        ? req.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.manager_name?.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      return deptMatch && statusMatch && fromMatch && toMatch && searchMatch;
    });
  }, [hiringRequests, selectedDepartment, selectedStatus, planDateFrom, planDateTo, searchTerm]);

  const hasActiveFilters = selectedDepartment !== 'all' || selectedStatus !== 'all' || planDateFrom || planDateTo || searchTerm;

  const clearFilters = () => {
    setSelectedDepartment('all');
    setSelectedStatus('all');
    setPlanDateFrom('');
    setPlanDateTo('');
    setSearchTerm('');
  };

  if (isLoading) return <LoadingSpinner label="Loading Hiring Requests" />;

  const inputClass = "w-full px-3 py-2 rounded-xl text-sm outline-none transition";
  const inputStyle = { border: '1px solid rgba(18,76,114,0.25)', color: 'rgb(18,76,114)' };

  return (
    <div className='bg-white mx-4 sm:pt-4 sm:px-4 sm:rounded-t-[32px] md:pt-8 md:px-[calc(5%-1rem)] md:rounded-t-[40px] lg:px-[calc(6%-1rem)] xl:h-[100%] xl:pt-24 xl:px-[calc(2.5%-1rem)] xl:rounded-t-[48px] 2xl:px-[calc(8%-1rem)] 3xl:px-[calc(8%-1rem)] 4xl:px-[calc(10%-1rem)]'>
      <div className='relative xl:-top-[140px]'>
        <div className="bg-white rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 10px 40px rgba(18,76,114,0.10)', border: '1px solid rgba(18,76,114,0.08)' }}>

          {/* ── Header ─────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-6 py-4"
            style={{ borderBottom: '1px solid rgba(18,76,114,0.08)', background: 'linear-gradient(to right, rgba(18,76,114,0.04), transparent)' }}>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-primary-800/10 transition"
                style={{ backgroundColor: 'rgba(18,76,114,0.08)' }}>
                <svg className="w-5 h-5" style={{ color: 'rgb(18,76,114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h3 className="text-lg font-bold" style={{ color: 'rgb(18,76,114)' }}>Hiring Requests</h3>
                <p className="text-xs" style={{ color: 'rgba(18,76,114,0.5)' }}>
                  Showing <span className="font-semibold">{filteredHiringRequests.length}</span> of{' '}
                  <span className="font-semibold">{hiringRequests.length}</span> requests
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Active filter count badge */}
              {hasActiveFilters && (
                <button onClick={clearFilters}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{ backgroundColor: 'rgba(239,68,68,0.08)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear Filters
                </button>
              )}

              {/* Toggle Filters */}
              <button
                onClick={() => setFiltersExpanded(p => !p)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
                style={{
                  backgroundColor: filtersExpanded ? 'rgb(18,76,114)' : 'rgba(18,76,114,0.06)',
                  color: filtersExpanded ? 'white' : 'rgb(18,76,114)',
                  border: '1px solid rgba(18,76,114,0.15)'
                }}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                </svg>
                Filters
                {hasActiveFilters && (
                  <span className="w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center"
                    style={{ backgroundColor: filtersExpanded ? 'rgba(255,255,255,0.25)' : 'rgb(18,76,114)', color: filtersExpanded ? 'white' : 'white' }}>
                    {[selectedDepartment !== 'all', selectedStatus !== 'all', !!planDateFrom, !!planDateTo, !!searchTerm].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* ── Collapsible Filters ─────────────────────────────── */}
          {filtersExpanded && (
            <div className="px-6 py-4" style={{ backgroundColor: 'rgba(18,76,114,0.02)', borderBottom: '1px solid rgba(18,76,114,0.08)' }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">

                {/* Department */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(18,76,114,0.6)' }}>Department</label>
                  <Select
                    options={[{ value: 'all', label: 'All Departments' }, ...departments.map(d => ({ value: d.department_id, label: d.department_name }))]}
                    value={selectedDepartment === 'all'
                      ? { value: 'all', label: 'All Departments' }
                      : { value: selectedDepartment, label: departments.find(d => Number(d.department_id) === Number(selectedDepartment))?.department_name }}
                    onChange={option => setSelectedDepartment(option.value)}
                    styles={customSelectStyles}
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(18,76,114,0.6)' }}>Status</label>
                  <Select
                    options={[
                      { value: 'all', label: 'All Statuses' },
                      ...['Draft','Submitted','Approved','Rejected','On Hold','Sourcing',
                          'Shortlisting','Shortlisted','Interviewing','Hiring Approvals','Offering','Joining','Joined'
                        ].map(s => ({ value: s, label: s }))
                    ]}
                    value={selectedStatus === 'all' ? { value: 'all', label: 'All Statuses' } : { value: selectedStatus, label: selectedStatus }}
                    onChange={option => setSelectedStatus(option.value)}
                    styles={customSelectStyles}
                  />
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(18,76,114,0.6)' }}>Plan Date From</label>
                  <input type="date" value={planDateFrom} onChange={e => setPlanDateFrom(e.target.value)}
                    className={inputClass} style={inputStyle}
                    onFocus={e => e.target.style.boxShadow = '0 0 0 3px rgba(18,76,114,0.12)'}
                    onBlur={e => e.target.style.boxShadow = 'none'} />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(18,76,114,0.6)' }}>Plan Date To</label>
                  <input type="date" value={planDateTo} onChange={e => setPlanDateTo(e.target.value)}
                    className={inputClass} style={inputStyle}
                    onFocus={e => e.target.style.boxShadow = '0 0 0 3px rgba(18,76,114,0.12)'}
                    onBlur={e => e.target.style.boxShadow = 'none'} />
                </div>

                {/* Search */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(18,76,114,0.6)' }}>Search</label>
                  <div className="relative">
                    <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                      className={`${inputClass} pl-9`} style={inputStyle}
                      placeholder="Title or Manager..."
                      onFocus={e => e.target.style.boxShadow = '0 0 0 3px rgba(18,76,114,0.12)'}
                      onBlur={e => e.target.style.boxShadow = 'none'} />
                    <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(18,76,114,0.4)' }}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Results ─────────────────────────────────────────── */}
          <div className="p-4 sm:p-6">
            {filteredHiringRequests.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(18,76,114,0.06)' }}>
                  <svg className="w-7 h-7" style={{ color: 'rgba(18,76,114,0.3)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="font-semibold" style={{ color: 'rgb(18,76,114)' }}>No requests found</p>
                <p className="text-sm" style={{ color: 'rgba(18,76,114,0.5)' }}>Try adjusting your filters</p>
                {hasActiveFilters && (
                  <button onClick={clearFilters}
                    className="mt-2 px-4 py-2 rounded-xl text-sm font-medium text-white transition-all"
                    style={{ backgroundColor: 'rgb(18,76,114)' }}>
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              <HiringRequestCards
                filteredHiringRequests={filteredHiringRequests}
                demandPlans={demandPlans}
                hiringManagers={hiringManagers}
                visibleCount={visibleCount}
                departments={departments}
              />
            )}

            {filteredHiringRequests.length > visibleCount && (
              <div className="flex justify-center mt-6">
                <button onClick={() => navigate('/hiring-request')}
                  className="px-6 py-2.5 text-white font-medium rounded-xl transition-all hover:opacity-90"
                  style={{ backgroundColor: 'rgb(18,76,114)', boxShadow: '0 4px 12px rgba(18,76,114,0.25)' }}>
                  View More
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default HiringRequest;